import { useState, useEffect } from 'react';
import api from '../services/api';

export default function FunnelPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const result = await api.getFunnel();
        setData(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading funnel: {error}
        </div>
      </div>
    );
  }

  const f = data?.funnel || {};
  const maxViews = Math.max(f.landing_page_views || 1, 1);

  const funnelSteps = [
    { label: 'Landing Page Views', value: f.landing_page_views || 0 },
    { label: 'Diagnostic Started', value: f.diagnostic_starts || 0 },
    { label: 'Diagnostic Completed', value: f.diagnostic_completions || 0 },
    { label: 'App CTA Clicks', value: f.app_cta_clicks || 0 },
    { label: 'Signups', value: f.signups || 0 },
  ];

  const bySource = f.by_utm_source || {};
  const sourceEntries = Object.entries(bySource).sort((a, b) => b[1].sessions - a[1].sessions);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-teacher-text-primary">Funnel Analytics</h1>
        <p className="text-teacher-text-secondary">Reel-to-signup conversion · {data?.metricsDate || 'Loading...'}</p>
        <p className="text-xs text-teacher-text-tertiary mt-1">
          Signup attribution requires the reel CTA to hand off <code className="font-mono">reel_tuid</code> to app.jeevibe.com.
          Mobile app installs (Play Store / App Store) are not attributed until an install-referrer SDK is wired.
        </p>
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        {funnelSteps.map((step, i) => {
          const convRate = i > 0 && funnelSteps[i-1].value > 0
            ? Math.round((step.value / funnelSteps[i-1].value) * 100)
            : null;
          return (
            <div key={step.label} className="bg-white rounded-xl p-5 shadow-sm border border-teacher-border">
              <h3 className="text-xs font-medium text-teacher-text-secondary mb-1">{step.label}</h3>
              <span className="text-2xl font-bold text-teacher-text-primary">
                {loading ? '...' : step.value.toLocaleString()}
              </span>
              {convRate !== null && (
                <p className="text-xs text-teacher-text-tertiary mt-1">{convRate}% of previous step</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel waterfall */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
          <h3 className="text-lg font-semibold text-teacher-text-primary mb-4">Funnel Waterfall</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {funnelSteps.map((step, i) => {
                const pct = (step.value / maxViews) * 100;
                const dropoff = i > 0 && funnelSteps[i-1].value > 0
                  ? Math.round((1 - step.value / funnelSteps[i-1].value) * 100)
                  : null;
                const colors = ['#3b82f6', '#60a5fa', '#818cf8', '#a78bfa', '#6d28d9'];
                return (
                  <div key={step.label} className="flex items-center gap-3">
                    <div className="w-36 text-xs text-teacher-text-secondary truncate">{step.label}</div>
                    <div className="flex-1 bg-teacher-bg-secondary rounded h-7 overflow-hidden">
                      <div
                        className="h-full rounded flex items-center px-2"
                        style={{ width: `${Math.max(pct, 6)}%`, backgroundColor: colors[i] }}
                      >
                        <span className="text-xs font-semibold text-white">{step.value}</span>
                      </div>
                    </div>
                    <div className="w-14 text-right">
                      {dropoff !== null && (
                        <span className="text-xs font-medium text-red-500">-{dropoff}%</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Attribution by source */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
          <h3 className="text-lg font-semibold text-teacher-text-primary mb-4">Attribution by Source</h3>
          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : sourceEntries.length === 0 ? (
            <div className="text-center py-12 text-teacher-text-tertiary">
              <p>No funnel sessions recorded yet</p>
              <p className="text-sm mt-1">Data populates when reels go live</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-teacher-border">
                    <th className="text-left py-2 text-xs font-semibold text-teacher-text-secondary uppercase">Source</th>
                    <th className="text-center py-2 text-xs font-semibold text-teacher-text-secondary uppercase">Sessions</th>
                    <th className="text-center py-2 text-xs font-semibold text-teacher-text-secondary uppercase">Signups</th>
                    <th className="text-center py-2 text-xs font-semibold text-teacher-text-secondary uppercase">Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceEntries.map(([source, stats]) => {
                    const conv = stats.sessions > 0 ? Math.round((stats.signups / stats.sessions) * 1000) / 10 : 0;
                    return (
                      <tr key={source} className="border-b border-gray-50">
                        <td className="py-2 font-medium text-teacher-text-primary capitalize">{source}</td>
                        <td className="py-2 text-center text-teacher-text-secondary">{stats.sessions}</td>
                        <td className="py-2 text-center text-teacher-text-secondary">{stats.signups}</td>
                        <td className="py-2 text-center">
                          <span className={`font-semibold ${conv >= 7 ? 'text-green-600' : conv >= 4 ? 'text-amber-600' : 'text-red-600'}`}>
                            {conv}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
